import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Undo2, Eye, RotateCcw, Monitor, RefreshCw, Tablet, Smartphone } from "lucide-react";
import { api, adminHeaders } from "../lib/api";
import ImageField from "./ImageField";
import FocalPointPicker from "./FocalPointPicker";
import { SPACING_SECTIONS, HERO_HEIGHTS, OVERLINE_COLORS, SPACING_PRESETS, balancedSpacingMap } from "../lib/layout";

const labelCls = "block font-mono text-[10px] uppercase tracking-[0.2em] text-vellum-faint mb-2";
const inputCls = "w-full bg-transparent border border-line px-4 py-3 text-vellum text-sm focus:border-crimson outline-none transition-colors";

function Field({ label, value, onChange, textarea, rows = 3, testid }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {textarea ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} className={inputCls} data-testid={testid} />
      ) : (
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testid} />
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-line bg-ink p-6 mb-6">
      <h3 className="font-serif text-2xl text-vellum mb-6">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>
    </div>
  );
}

export default function SiteContentEditor({ onSaved }) {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [historyCount, setHistoryCount] = useState(0);
  const [hasDraft, setHasDraft] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewPage, setPreviewPage] = useState("/");
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [iframeKey, setIframeKey] = useState(0);

  useEffect(() => {
    api.get("/content").then((r) => setDraft(r.data)).catch(() => toast.error("Could not load content"));
    api.get("/admin/content/meta", { headers: adminHeaders() }).then((r) => { setHistoryCount(r.data.history_count); setHasDraft(r.data.has_draft); }).catch((e) => console.warn("Could not load content meta", e));
  }, []);

  // Auto-refresh the live preview a moment after edits stop (only when open).
  useEffect(() => {
    if (!showPreview || !draft) return undefined;
    const t = setTimeout(async () => {
      try {
        await api.put("/admin/content/draft", draft, { headers: adminHeaders() });
        setHasDraft(true);
        setIframeKey((k) => k + 1);
      } catch (e) {
        console.warn("Draft auto-save skipped", e);
      }
    }, 1200);
    return () => clearTimeout(t);
  }, [draft, showPreview]);

  if (!draft) return <p className="text-vellum-muted">Loading…</p>;

  const setSec = (sec, patch) => setDraft((d) => ({ ...d, [sec]: { ...d[sec], ...patch } }));
  const setItem = (i, patch) =>
    setDraft((d) => ({ ...d, manifesto: { ...d.manifesto, items: d.manifesto.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) } }));
  const setNav = (key, patch) =>
    setDraft((d) => ({ ...d, nav: { ...d.nav, [key]: { ...d.nav[key], ...patch } } }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/content", draft, { headers: adminHeaders() });
      setHistoryCount(r.data.history_count);
      setHasDraft(false);
      toast.success("Published live");
      onSaved?.();
    } catch {
      toast.error("Publish failed");
    } finally { setSaving(false); }
  };

  const previewDraft = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content/draft", draft, { headers: adminHeaders() });
      setHasDraft(true);
      toast.success("Draft saved — opening preview");
      window.open("/?preview=1", "_blank", "noopener");
    } catch {
      toast.error("Could not save draft");
    } finally { setSaving(false); }
  };

  const discardDraft = async () => {
    setSaving(true);
    try {
      await api.post("/admin/content/discard-draft", {}, { headers: adminHeaders() });
      setHasDraft(false);
      toast.success("Draft discarded");
    } catch {
      toast.error("Could not discard draft");
    } finally { setSaving(false); }
  };

  const revert = async () => {
    setSaving(true);
    try {
      const r = await api.post("/admin/content/undo", {}, { headers: adminHeaders() });
      setDraft(r.data.content);
      setHistoryCount(r.data.history_count);
      toast.success("Reverted to previous version");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.status === 400 ? "Nothing to revert" : "Revert failed");
    } finally { setSaving(false); }
  };

  const refreshPreview = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content/draft", draft, { headers: adminHeaders() });
      setHasDraft(true);
      setIframeKey((k) => k + 1);
      toast.success("Preview updated with your latest changes");
    } catch {
      toast.error("Could not update preview");
    } finally { setSaving(false); }
  };

  const h = draft.hero, p = draft.picks, fp = draft.flawed_prophets, ft = draft.footer;
  const pages = draft.pages || {};
  const setPage = (key, patch) => setDraft((d) => ({ ...d, pages: { ...d.pages, [key]: { ...(d.pages?.[key] || {}), ...patch } } }));

  return (
    <div data-testid="site-content-editor">
      <div className="flex items-center justify-between mb-2 gap-4">
        <p className="text-sm text-vellum-muted max-w-md">Edit the site&apos;s editorial text and images. Preview a draft privately, then publish it live.</p>
        <div className="flex items-center gap-3 shrink-0">
          <button onClick={revert} disabled={saving || historyCount === 0} data-testid="content-revert" title={historyCount === 0 ? "No previous version to revert to" : "Undo the last published version"} className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 border border-line text-vellum hover:border-crimson hover:text-crimson transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            <Undo2 className="w-4 h-4" /> Revert last save
          </button>
          <button onClick={() => setShowPreview((s) => !s)} data-testid="content-toggle-preview" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 border border-line text-vellum hover:border-brass hover:text-brass transition-colors">
            <Monitor className="w-4 h-4" /> {showPreview ? "Hide preview" : "Live preview"}
          </button>
          <button onClick={previewDraft} disabled={saving} data-testid="content-preview" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 border border-brass text-brass hover:bg-brass hover:text-ink transition-colors disabled:opacity-40">
            <Eye className="w-4 h-4" /> Preview draft
          </button>
          <button onClick={save} disabled={saving} data-testid="content-save" className="font-mono text-xs uppercase tracking-[0.2em] px-6 py-3 bg-vellum text-ink hover:bg-crimson hover:text-onaccent transition-colors disabled:opacity-50">
            {saving ? "Saving…" : "Publish live"}
          </button>
        </div>
      </div>

      {hasDraft && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 border border-brass/40 bg-brass/5 px-4 py-3" data-testid="draft-notice">
          <p className="text-sm text-brass">You have an unpublished draft. <a href="/?preview=1" target="_blank" rel="noreferrer" className="underline hover:text-onaccent" data-testid="draft-open-preview">Open preview</a> or publish to make it live.</p>
          <button onClick={discardDraft} disabled={saving} data-testid="draft-discard" className="font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border border-line text-vellum-muted hover:border-crimson hover:text-crimson transition-colors">
            Discard draft
          </button>
        </div>
      )}

      {showPreview && (
        <div className="mb-6 border border-brass/40 bg-ink" data-testid="live-preview-panel">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3">
            <div className="flex items-center gap-2">
              {[
                { to: "/", label: "Home" },
                { to: "/library", label: "Library" },
                { to: "/curriculums", label: "Curriculums" },
              ].map((pg) => (
                <button
                  key={pg.to}
                  onClick={() => { setPreviewPage(pg.to); setIframeKey((k) => k + 1); }}
                  data-testid={`preview-page-${pg.label.toLowerCase()}`}
                  className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 border transition-colors ${previewPage === pg.to ? "border-brass text-brass" : "border-line text-vellum-muted hover:text-vellum"}`}
                >
                  {pg.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                {[
                  { key: "desktop", Icon: Monitor },
                  { key: "tablet", Icon: Tablet },
                  { key: "phone", Icon: Smartphone },
                ].map(({ key, Icon }) => (
                  <button
                    key={key}
                    onClick={() => setPreviewDevice(key)}
                    data-testid={`preview-device-${key}`}
                    title={key}
                    className={`p-2 border transition-colors ${previewDevice === key ? "border-brass text-brass" : "border-line text-vellum-muted hover:text-vellum"}`}
                  >
                    <Icon className="w-4 h-4" />
                  </button>
                ))}
              </div>
              <button onClick={refreshPreview} disabled={saving} data-testid="preview-refresh" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 bg-brass text-ink hover:opacity-90 transition-opacity disabled:opacity-40">
                <RefreshCw className="w-3 h-3" /> Refresh now
              </button>
            </div>
          </div>
          <p className="px-4 pt-3 text-xs text-vellum-faint">Auto-updates a moment after you stop editing. Use “Refresh now” to update immediately. Publishing makes it live.</p>
          <div className="p-4 bg-ink-800 flex justify-center">
            <div className="transition-all duration-300 w-full" style={{ maxWidth: previewDevice === "phone" ? "390px" : previewDevice === "tablet" ? "820px" : "100%" }}>
              <iframe
                key={`${iframeKey}-${previewDevice}`}
                title="Live preview"
                src={`${previewPage}?preview=1`}
                data-testid="live-preview-iframe"
                className="w-full bg-white border border-line"
                style={{ height: previewDevice === "phone" ? "680px" : "580px" }}
              />
            </div>
          </div>
        </div>
      )}

      <Section title="Home — Hero">
        <Field label="Overline" value={h.overline} onChange={(v) => setSec("hero", { overline: v })} testid="hero-overline" />
        <div className="md:col-span-2"><label className={labelCls}>Background image</label><ImageField value={h.bg_image} onChange={(v) => setSec("hero", { bg_image: v })} testid="hero-bg" aspect="wide" /><FocalPointPicker image={h.bg_image} value={h.bg_focal} onChange={(v) => setSec("hero", { bg_focal: v })} testid="hero-focal" showZoom showVeil /></div>
        <Field label="Title line 1" value={h.title_line1} onChange={(v) => setSec("hero", { title_line1: v })} testid="hero-title1" />
        <Field label="Title line 2" value={h.title_line2} onChange={(v) => setSec("hero", { title_line2: v })} testid="hero-title2" />
        <Field label="Highlighted line" value={h.highlight} onChange={(v) => setSec("hero", { highlight: v })} testid="hero-highlight" />
        <div className="md:col-span-2"><Field label="Subtitle paragraph" value={h.subtitle} onChange={(v) => setSec("hero", { subtitle: v })} textarea testid="hero-subtitle" /></div>
        <div>
          <label className={labelCls}>Hero banner height</label>
          <select value={h.height || "full"} onChange={(e) => setSec("hero", { height: e.target.value })} data-testid="hero-height" className={inputCls}>
            {HERO_HEIGHTS.map((o) => <option key={o.key} value={o.key} className="bg-ink">{o.label}</option>)}
          </select>
        </div>
      </Section>

      <Section title="Home — Manifesto">
        <div className="md:col-span-2"><Field label="Section label" value={draft.manifesto.label} onChange={(v) => setDraft((d) => ({ ...d, manifesto: { ...d.manifesto, label: v } }))} testid="mani-label" /></div>
        {draft.manifesto.items.map((it, i) => (
          <div key={it.n || i} className="md:col-span-2 border-t border-line pt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label={`#${it.n} — Number`} value={it.n} onChange={(v) => setItem(i, { n: v })} testid={`mani-${i}-n`} />
            <Field label="Title" value={it.t} onChange={(v) => setItem(i, { t: v })} testid={`mani-${i}-title`} />
            <div className="md:col-span-2"><Field label="Body" value={it.b} onChange={(v) => setItem(i, { b: v })} textarea testid={`mani-${i}-body`} /></div>
          </div>
        ))}
      </Section>

      <Section title="Home — Marquee">
        <div className="md:col-span-2">
          <label className={labelCls}>Scrolling words (one per line)</label>
          <textarea
            value={(draft.marquee?.words || []).join("\n")}
            onChange={(e) => setDraft((d) => ({ ...d, marquee: { ...d.marquee, words: e.target.value.split("\n").map((w) => w.trim()).filter(Boolean) } }))}
            rows={5}
            className={inputCls}
            data-testid="marquee-words"
          />
        </div>
      </Section>

      <Section title="Home — Navigation buttons">
        <Field label="Library button label" value={draft.nav?.library?.title} onChange={(v) => setNav("library", { title: v })} testid="nav-library-title" />
        <Field label="Curriculums button label" value={draft.nav?.curriculums?.title} onChange={(v) => setNav("curriculums", { title: v })} testid="nav-curriculums-title" />
        <div className="md:col-span-2">
          <label className={labelCls}>Which button comes first</label>
          <select
            value={(draft.nav?.order || ["library", "curriculums"])[0]}
            onChange={(e) => { const first = e.target.value; const other = first === "library" ? "curriculums" : "library"; setDraft((d) => ({ ...d, nav: { ...d.nav, order: [first, other] } })); }}
            data-testid="nav-order"
            className={inputCls}
          >
            <option value="library" className="bg-ink">Library first</option>
            <option value="curriculums" className="bg-ink">Curriculums first</option>
          </select>
        </div>
      </Section>

      <Section title="Home — Picks of the Month (headings)">
        <Field label="Label" value={p.label} onChange={(v) => setSec("picks", { label: v })} testid="picks-label" />
        <div />
        <Field label="Heading line 1" value={p.heading_line1} onChange={(v) => setSec("picks", { heading_line1: v })} testid="picks-heading1" />
        <Field label="Heading line 2 (italic)" value={p.heading_line2} onChange={(v) => setSec("picks", { heading_line2: v })} testid="picks-heading2" />
        <div className="md:col-span-2"><Field label="Blurb" value={p.blurb} onChange={(v) => setSec("picks", { blurb: v })} textarea testid="picks-blurb" /></div>
      </Section>

      <Section title="Home — Flawed Prophets (your book)">
        <Field label="Label" value={fp.label} onChange={(v) => setSec("flawed_prophets", { label: v })} testid="fp-label" />
        <div><label className={labelCls}>Book image</label><ImageField value={fp.image} onChange={(v) => setSec("flawed_prophets", { image: v })} testid="fp-image" /></div>
        <Field label="Title line 1" value={fp.title_line1} onChange={(v) => setSec("flawed_prophets", { title_line1: v })} testid="fp-title1" />
        <Field label="Title line 2 (italic)" value={fp.title_line2} onChange={(v) => setSec("flawed_prophets", { title_line2: v })} testid="fp-title2" />
        <div className="md:col-span-2"><Field label="Description" value={fp.description} onChange={(v) => setSec("flawed_prophets", { description: v })} textarea testid="fp-description" /></div>
        <Field label="Amazon URL" value={fp.amazon_url} onChange={(v) => setSec("flawed_prophets", { amazon_url: v })} testid="fp-amazon-url" />
        <Field label="Goodreads URL" value={fp.goodreads_url} onChange={(v) => setSec("flawed_prophets", { goodreads_url: v })} testid="fp-goodreads-url" />
      </Section>

      <Section title="Library page — hero">
        <Field label="Overline (auto-appends · N entries)" value={pages.library?.overline} onChange={(v) => setPage("library", { overline: v })} testid="page-library-overline" />
        <div />
        <Field label="Title line 1" value={pages.library?.title_line1} onChange={(v) => setPage("library", { title_line1: v })} testid="page-library-title1" />
        <Field label="Title line 2 (italic)" value={pages.library?.title_line2} onChange={(v) => setPage("library", { title_line2: v })} testid="page-library-title2" />
        <div className="md:col-span-2"><Field label="Subtitle paragraph" value={pages.library?.subtitle} onChange={(v) => setPage("library", { subtitle: v })} textarea testid="page-library-subtitle" /></div>
        <div className="md:col-span-2"><label className={labelCls}>Image</label><ImageField value={pages.library?.image} onChange={(v) => setPage("library", { image: v })} testid="page-library" aspect="wide" /><FocalPointPicker image={pages.library?.image} value={pages.library?.focal} onChange={(v) => setPage("library", { focal: v })} testid="page-library-focal" showZoom showVeil /></div>
        <div>
          <label className={labelCls}>Hero banner height</label>
          <select value={pages.library?.hero_height || "standard"} onChange={(e) => setPage("library", { hero_height: e.target.value })} data-testid="page-library-height" className={inputCls}>
            {HERO_HEIGHTS.map((o) => <option key={o.key} value={o.key} className="bg-ink">{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Overline colour</label>
          <select value={pages.library?.overline_color || "crimson"} onChange={(e) => setPage("library", { overline_color: e.target.value })} data-testid="page-library-overline-color" className={inputCls}>
            {OVERLINE_COLORS.map((o) => <option key={o.key} value={o.key} className="bg-ink">{o.label}</option>)}
          </select>
        </div>
      </Section>

      <Section title="Curriculums page — hero">
        <div className="md:col-span-2"><Field label="Overline (small label under the title)" value={pages.curriculums?.overline} onChange={(v) => setPage("curriculums", { overline: v })} testid="page-curriculums-overline" /></div>
        <Field label="Title line 1" value={pages.curriculums?.title_line1} onChange={(v) => setPage("curriculums", { title_line1: v })} testid="page-curriculums-title1" />
        <Field label="Title line 2 (italic)" value={pages.curriculums?.title_line2} onChange={(v) => setPage("curriculums", { title_line2: v })} testid="page-curriculums-title2" />
        <div className="md:col-span-2"><Field label="Subtitle paragraph" value={pages.curriculums?.subtitle} onChange={(v) => setPage("curriculums", { subtitle: v })} textarea testid="page-curriculums-subtitle" /></div>
        <div className="md:col-span-2"><label className={labelCls}>Image</label><ImageField value={pages.curriculums?.image} onChange={(v) => setPage("curriculums", { image: v })} testid="page-curriculums" aspect="wide" /><FocalPointPicker image={pages.curriculums?.image} value={pages.curriculums?.focal} onChange={(v) => setPage("curriculums", { focal: v })} testid="page-curriculums-focal" showZoom showVeil /></div>
        <div>
          <label className={labelCls}>Hero banner height</label>
          <select value={pages.curriculums?.hero_height || "standard"} onChange={(e) => setPage("curriculums", { hero_height: e.target.value })} data-testid="page-curriculums-height" className={inputCls}>
            {HERO_HEIGHTS.map((o) => <option key={o.key} value={o.key} className="bg-ink">{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Overline colour</label>
          <select value={pages.curriculums?.overline_color || "crimson"} onChange={(e) => setPage("curriculums", { overline_color: e.target.value })} data-testid="page-curriculums-overline-color" className={inputCls}>
            {OVERLINE_COLORS.map((o) => <option key={o.key} value={o.key} className="bg-ink">{o.label}</option>)}
          </select>
        </div>
      </Section>

      <Section title="Layout &amp; Spacing — gaps between sections">
        <div className="md:col-span-2 flex flex-wrap items-start justify-between gap-3">
          <p className="text-sm text-vellum-muted max-w-lg">Set the empty space above and below each section (in pixels). Leave blank to keep the default spacing. Lower numbers tighten the gap; higher numbers add more breathing room.</p>
          <button
            type="button"
            onClick={() => setDraft((d) => ({ ...d, spacing: balancedSpacingMap() }))}
            data-testid="spacing-reset-all"
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border border-line text-vellum-muted hover:border-crimson hover:text-crimson transition-colors shrink-0"
          >
            <RotateCcw className="w-3 h-3" /> Reset all to default
          </button>
        </div>
        <div className="md:col-span-2 flex flex-wrap items-center gap-2 border border-line bg-ink-800 px-4 py-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-vellum-faint mr-1">Quick presets:</span>
          {SPACING_PRESETS.map((preset) => (
            <button
              key={preset.key}
              type="button"
              onClick={() => setDraft((d) => ({ ...d, spacing: preset.map ? { ...preset.map } : balancedSpacingMap() }))}
              data-testid={`spacing-preset-${preset.key}`}
              className="font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border border-line text-vellum hover:border-brass hover:text-brass transition-colors"
            >
              {preset.label}
            </button>
          ))}
          <span className="font-mono text-[10px] text-vellum-faint ml-1">— sets sensible gaps site-wide, then fine-tune below</span>
        </div>
        {SPACING_SECTIONS.map((sec) => {
          const val = draft.spacing?.[sec.key] || {};
          const setSpace = (patch) => setDraft((d) => ({ ...d, spacing: { ...(d.spacing || {}), [sec.key]: { ...(d.spacing?.[sec.key] || {}), ...patch } } }));
          const num = (v) => (v === "" || v === null || v === undefined ? null : Math.max(0, Math.min(400, parseInt(v, 10) || 0)));
          return (
            <div key={sec.key} className="md:col-span-2 border-t border-line pt-4 grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <p className="overline text-crimson sm:pb-3">{sec.label}</p>
              <div>
                <label className={labelCls}>Space above (px)</label>
                <input type="number" min="0" max="400" value={Number.isFinite(val.top) ? val.top : ""} placeholder="default" onChange={(e) => setSpace({ top: num(e.target.value) })} data-testid={`spacing-${sec.key}-top`} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Space below (px)</label>
                <input type="number" min="0" max="400" value={Number.isFinite(val.bottom) ? val.bottom : ""} placeholder="default" onChange={(e) => setSpace({ bottom: num(e.target.value) })} data-testid={`spacing-${sec.key}-bottom`} className={inputCls} />
              </div>
            </div>
          );
        })}
      </Section>

      <Section title="Footer">
        <Field label="Book link label" value={ft.book_link_label} onChange={(v) => setSec("footer", { book_link_label: v })} />
        <Field label="Book link URL" value={ft.book_link_url} onChange={(v) => setSec("footer", { book_link_url: v })} />
        <div className="md:col-span-2"><Field label="Tagline" value={ft.tagline} onChange={(v) => setSec("footer", { tagline: v })} testid="footer-tagline" /></div>
        <Field label="Instagram URL" value={ft.instagram_url} onChange={(v) => setSec("footer", { instagram_url: v })} testid="footer-instagram" />
        <Field label="YouTube URL" value={ft.youtube_url} onChange={(v) => setSec("footer", { youtube_url: v })} testid="footer-youtube" />
        <div className="md:col-span-2"><Field label="Email address (Gmail)" value={ft.email} onChange={(v) => setSec("footer", { email: v })} testid="footer-email" /></div>
      </Section>

      <div className="flex gap-3">
        <button onClick={previewDraft} disabled={saving} data-testid="content-preview-bottom" className="flex-1 inline-flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-[0.2em] py-4 border border-brass text-brass hover:bg-brass hover:text-ink transition-colors disabled:opacity-50">
          <Eye className="w-4 h-4" /> Preview draft
        </button>
        <button onClick={save} disabled={saving} data-testid="content-save-bottom" className="flex-1 font-mono text-xs uppercase tracking-[0.2em] py-4 bg-vellum text-ink hover:bg-crimson hover:text-onaccent transition-colors disabled:opacity-50">
          {saving ? "Saving…" : "Publish live"}
        </button>
      </div>
    </div>
  );
}
